import React, { useEffect, useRef, useState } from 'react';
import Button, { IconButton } from './Button';
import styles from '../styles/Share.module.css';
import FaIcon from './FaIcon';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  RedditIcon,
  RedditShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { useGlobalModal } from '../contexts/ModalContext';

export const isWebShareSupported = () => {
  if (typeof navigator === 'undefined') return false;
  return typeof navigator.share === 'function';
};

const addShareUTMSource = (url) => {
    if (!url) return '';
    const urlObj = new URL(url);
    urlObj.searchParams.set('utm_source', 'share');
    return urlObj.toString();
};

const copyTextWithExecCommandFallback = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    // fallback handled below
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const copied = typeof document.execCommand === 'function' && document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied === true;
  } catch (_) {
    return false;
  }
};

export const ShareOptions = ({ sharedUrl, title, text, copyText, copyLabel, downloadFile,
  onShareSuccess, onShareError, onShareOutcome, onChannelClick, inline = false,
}) => {
  const [copyMessage, setCopyMessage] = useState('');
  const [copyError, setCopyError] = useState(false);

  const handleCopyLink = async () => {
    const copied = await copyTextWithExecCommandFallback(copyText ? `${copyText}\n${sharedUrl}` : sharedUrl);
    if (copied) {
      onShareOutcome?.({ outcome: 'copied', channel: 'clipboard' });
      setCopyError(false);
      setCopyMessage(copyText ? 'Risultato copiato!' : 'Link copiato!');
      if (onShareSuccess) {
        onShareSuccess();
      }
      return;
    }

    onShareOutcome?.({ outcome: 'error', channel: 'clipboard' });
    const error = new Error(copyText ? 'Impossibile copiare il risultato.' : 'Impossibile copiare il link.');
    setCopyError(true);
    setCopyMessage(error.message);
    if (onShareError) {
      onShareError(error);
    }
  };

  return (
    <div className={`${styles.fallbackModalContent} ${inline ? styles.inlineShareContent : ''}`}>
      <div className={`${styles.channelGrid} ${downloadFile ? styles.downloadGrid : ''}`}>
        <WhatsappShareButton
          url={sharedUrl}
          title={text}
          onClick={() => onChannelClick('whatsapp')}
          className={styles.channelButton}
          aria-label="Condividi su WhatsApp"
          title="Condividi su WhatsApp"
        >
          <WhatsappIcon round size={36} />
          <span className={styles.channelLabel}>WhatsApp</span>
        </WhatsappShareButton>
        <button
          type="button"
          data-testid="copy-link-button"
          className={styles.copyIconButton}
          aria-label={copyLabel}
          title={copyLabel}
          onClick={handleCopyLink}
        >
          <span className={styles.copyIconCircle}>
            <FaIcon className="fa fa-link" />
          </span>
          <span className={styles.channelLabel}>{copyLabel}</span>
        </button>
        {downloadFile && (
          <button type="button" className={styles.copyIconButton} onClick={() => {
            try {
              const objectUrl = URL.createObjectURL(downloadFile);
              const anchor = document.createElement('a');
              anchor.href = objectUrl;
              anchor.download = downloadFile.name || 'giochini-traguardo.png';
              document.body.appendChild(anchor);
              anchor.click();
              anchor.remove();
              setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
              onShareOutcome?.({ outcome: 'downloaded', channel: 'download' });
            } catch (error) {
              onShareOutcome?.({ outcome: 'error', channel: 'download' });
              onShareError?.(error);
            }
          }}>
            <span className={styles.copyIconCircle}><FaIcon className="fa fa-download" /></span>
            <span className={styles.channelLabel}>Scarica immagine</span>
          </button>
        )}
        <FacebookShareButton
          url={sharedUrl}
          quote={text}
          onClick={() => onChannelClick('facebook')}
          className={styles.channelButton}
          aria-label="Condividi su Facebook"
          title="Condividi su Facebook"
        >
          <FacebookIcon round size={36} />
          <span className={styles.channelLabel}>Facebook</span>
        </FacebookShareButton>
        <RedditShareButton
          url={sharedUrl}
          title={text}
          onClick={() => onChannelClick('reddit')}
          className={styles.channelButton}
          aria-label="Condividi su Reddit"
          title="Condividi su Reddit"
        >
          <RedditIcon round size={36} />
          <span className={styles.channelLabel}>Reddit</span>
        </RedditShareButton>
        <LinkedinShareButton
          url={sharedUrl}
          title={title}
          summary={text}
          source={sharedUrl}
          onClick={() => onChannelClick('linkedin')}
          className={styles.channelButton}
          aria-label="Condividi su LinkedIn"
          title="Condividi su LinkedIn"
        >
          <LinkedinIcon round size={36} />
          <span className={styles.channelLabel}>LinkedIn</span>
        </LinkedinShareButton>
        <EmailShareButton
          url={sharedUrl}
          subject={title}
          body={text}
          onClick={() => onChannelClick('email')}
          className={styles.channelButton}
          aria-label="Condividi via email"
          title="Condividi via email"
        >
          <EmailIcon round size={36} />
          <span className={styles.channelLabel}>Email</span>
        </EmailShareButton>

      </div>
      {copyMessage && (
        <p className={copyError ? styles.copyMessageError : styles.copyMessageSuccess}>
          {copyMessage}
        </p>
      )}
    </div>
  );
};

const Share = ({ 
  title = 'Condividi', 
  text = "Dai un'occhiata a questo fantastico gioco!", 
  url = typeof window !== 'undefined' ? window.location.href : '',
  className = '',
  onShareSuccess,
  onShareError,
  onShareClick,
  copyText,
  copyLabel = 'Copia link',
  onFallbackOpen,
  onFallbackClose,
  files = [],
  downloadFile = null,
  fallbackPresentation = 'modal',
  replaceButtonOnFallback = false,
  inlineFallbackHeading,
  onShareOutcome,
  onShareCancel,
  alignToGame = false,
  disabled = false,
  primary = true,
  secondary = false,
  buttonLabel = 'Condividi',
  variant = 'button',
  iconClassName = 'fa fa-share-alt',
  iconTitle = 'Condividi',
  ...rest
}) => {
  const { openModal, closeModal } = useGlobalModal();
  const [isSharing, setIsSharing] = useState(false);
  const [showInlineFallback, setShowInlineFallback] = useState(false);
  const inlineFallbackRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  useEffect(() => {
    if (showInlineFallback && replaceButtonOnFallback) {
      inlineFallbackRef.current?.querySelector('button')?.focus();
    }
  }, [showInlineFallback, replaceButtonOnFallback]);
  const sharedUrl = addShareUTMSource(url);

  const handleChannelClick = (channel) => {
    if (fallbackPresentation !== 'inline') closeModal(`${channel}_share_click`);
    onShareOutcome?.({ outcome: 'channel_intent', channel });
    if (onShareSuccess) {
      onShareSuccess();
    }
  };

  const shareOptions = <ShareOptions sharedUrl={sharedUrl} title={title} text={text}
    copyText={copyText} copyLabel={copyLabel} downloadFile={downloadFile}
    onShareSuccess={onShareSuccess} onShareError={onShareError} onShareOutcome={onShareOutcome}
    onChannelClick={handleChannelClick} inline={fallbackPresentation === 'inline'} />;

  const openFallbackModal = () => {
    if (!mountedRef.current) return;
    onFallbackOpen?.();
    if (fallbackPresentation === 'inline') {
      setShowInlineFallback(true);
      return;
    }
    openModal({
      title: 'Condividi',
      content: shareOptions,
      alignToGame,
      onClose: onFallbackClose,
      analytics: {
        source_surface: 'share_button',
        modal_name: 'share_options',
        trigger: 'fallback_modal_opened',
      },
    });
  };

  const handleShare = async () => {
    if (disabled) {
      return;
    }

    if (onShareClick) {
      onShareClick();
    }

    if (!isWebShareSupported()) {
      openFallbackModal();
      return;
    }

    setIsSharing(true);

    try {
      const shareData = {
        title,
        text,
        url: sharedUrl,
      };

      // Files are prepared before this click; preserve native user activation.
      try {
        if (files.length && typeof navigator.canShare === 'function' && navigator.canShare({ files })) {
          shareData.files = files;
        }
      } catch (_) {}
      await navigator.share(shareData);
      onShareOutcome?.({ outcome: 'native_handoff', channel: 'native', includes_image: !!shareData.files });
      
      if (onShareSuccess) {
        onShareSuccess();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        onShareCancel?.();
        onShareOutcome?.({ outcome: 'cancelled', channel: 'native' });
      } else {
        onShareOutcome?.({ outcome: 'error', channel: 'native' });
      }
      // User cancelled or error occurred
      if (error.name !== 'AbortError' && onShareError) {
        onShareError(error);
      }
      // Native aborts can also mean blocked file sharing (e.g. Chrome Incognito).
      if (error.name !== 'AbortError' || fallbackPresentation === 'inline') openFallbackModal();
    } finally {
      if (mountedRef.current) setIsSharing(false);
    }
  };

  const inlineFallback = showInlineFallback && (
    <div ref={inlineFallbackRef} className={styles.inlineFallback}
      role="group" aria-label={inlineFallbackHeading || 'Opzioni di condivisione'}>
      {inlineFallbackHeading && <h4 className={styles.inlineFallbackHeading}>{inlineFallbackHeading}</h4>}
      {shareOptions}
    </div>
  );

  if (showInlineFallback && replaceButtonOnFallback) return inlineFallback;

  if (variant === 'icon') {
    return (
      <>
      <IconButton
        iconClassName={iconClassName}
        onClick={handleShare}
        disabled={disabled || isSharing}
        className={`${styles.shareIconButton} ${className}`}
        title={iconTitle}
        {...rest}
      />
      {inlineFallback}
      </>
    );
  }

  const buttonClass = `${styles.shareButton} ${className}`;

  return (
    <>
    <Button
      onClick={handleShare}
      disabled={disabled || isSharing}
      primary={primary}
      secondary={secondary}
      className={buttonClass}
      {...rest}
    >
      <FaIcon className="fa fa-share-alt"  />
      {isSharing ? 'Condivisione...' : buttonLabel}
    </Button>
    {inlineFallback}
    </>
  );
};

export default Share; 
